import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminAPI, apiClient } from '../../services/api';
import LoadingSpinner from '../../components/LoadingSpinner';
import PageLayout from '../../components/PageLayout';
import CreateInvoiceModal from '../../components/CreateInvoiceModal';
import {
  AdminEmptyState,
  AdminFilterCard,
  AdminModalShell,
  AdminPageHeader,
  AdminStatCard,
  AdminStatGrid,
  AdminTableCard,
} from '../../components/AdminSurface';
import toast from 'react-hot-toast';
import {
  DocumentTextIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ArrowDownTrayIcon,
  PlusIcon,
  FunnelIcon,
  UserIcon,
  ChartBarIcon,
  ShieldExclamationIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import CustomerDetailDrawer from '../../components/customer/CustomerDetailDrawer';
import BillingRevenueTrendChart from '../../components/charts/BillingRevenueTrendChart';
import { ManualPaymentDetails, promptForManualPaymentDetails } from '../../utils/manualPaymentPrompt';

function AdminBilling() {
  const { user } = useAuth();
  const showFeeComparison = Boolean(user?.is_platform_admin);
  const [activeTab, setActiveTab] = useState<'invoices' | 'disputes'>('invoices');
  const [disputeFilters, setDisputeFilters] = useState({ status: '', page: 1, limit: 20 });
  const [filters, setFilters] = useState({
    status: '',
    customerId: '',
    startDate: '',
    endDate: '',
    minAmount: '',
    maxAmount: '',
    page: 1,
    limit: 25
  });
  const [showCreateInvoice, setShowCreateInvoice] = useState(false);
  const [selectedFeeInvoice, setSelectedFeeInvoice] = useState<any | null>(null);
  const [selectedFeeCustomer, setSelectedFeeCustomer] = useState<any | null>(null);

  const queryClient = useQueryClient();

  // Fetch invoices from API
  const { data: invoicesData, isLoading: invoicesLoading, refetch: refetchInvoices } = useQuery(
    ['admin-invoices', filters],
    async () => {
      const response = await apiClient.get('/invoices/admin/all', {
        params: {
          status: filters.status || undefined,
          search: filters.customerId || undefined,
          page: filters.page,
          limit: filters.limit,
        },
      });
      return response.data;
    },
    { 
      select: (response) => ({
        invoices: response.invoices || [],
        pagination: response.pagination || {},
        total: response.total || 0
      }),
      keepPreviousData: true
    }
  );

  const { data: billingOverview } = useQuery(
    'admin-billing-overview',
    () => adminAPI.getBillingOverview(),
    { select: (response) => response.data }
  );

  const { data: paidInvoicesForTrend } = useQuery(
    'admin-paid-invoices-14d-trend',
    async () => {
      const response = await apiClient.get('/invoices/admin/all', {
        params: {
          status: 'paid',
          page: 1,
          limit: 500,
        },
      });
      return response.data?.invoices || [];
    },
    {
      staleTime: 60_000,
    }
  );

  // Fetch disputes
  const { data: disputesData, isLoading: disputesLoading } = useQuery(
    ['admin-disputes', disputeFilters],
    async () => {
      const response = await adminAPI.getDisputes(disputeFilters);
      return response.data;
    },
    {
      enabled: activeTab === 'disputes',
      keepPreviousData: true,
    }
  );

  const sendInvoiceMutation = useMutation(
    (invoiceId: string) => adminAPI.sendInvoice(invoiceId),
    {
      onSuccess: () => {
        toast.success('Invoice sent successfully');
      },
      onError: () => {
        toast.error('Failed to send invoice');
      }
    }
  );

  const markPaidMutation = useMutation(
    ({ invoiceId, paymentDetails }: { invoiceId: string; paymentDetails: ManualPaymentDetails }) =>
      adminAPI.markInvoicePaid(invoiceId, paymentDetails),
    {
      onSuccess: () => {
        toast.success('Invoice marked as paid');
        queryClient.invalidateQueries('admin-invoices');
      },
      onError: () => {
        toast.error('Failed to mark invoice as paid');
      }
    }
  );

  const handleMarkPaid = (invoiceId: string) => {
    const paymentDetails = promptForManualPaymentDetails(toast.error);
    if (!paymentDetails) return;

    markPaidMutation.mutate({ invoiceId, paymentDetails });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid': return 'bg-success-container text-success-on-container';
      case 'open': return 'bg-warning-container text-warning-on-container';
      case 'overdue': return 'bg-error-container text-error-on-container';
      case 'draft': return 'bg-surface-container-low text-on-surface-variant';
      default: return 'bg-surface-container-low text-on-surface-variant';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'paid': return <CheckCircleIcon className="w-5 h-5 text-green-500" />;
      case 'open': return <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500" />;
      case 'overdue': return <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />;
      default: return <DocumentTextIcon className="w-5 h-5 text-on-surface-variant" />;
    }
  };

  const getInvoiceStatusLabel = (status: string, isOverdue: boolean = false) => {
    if (isOverdue) return 'Overdue';
    if (status === 'open') return 'Pending payment';
    return status ? status.replace(/_/g, ' ').replace(/^\w/, (char) => char.toUpperCase()) : 'Unknown';
  };

  const formatCurrency = (amount: string | number) => {
    const normalized = typeof amount === 'string' ? Number.parseFloat(amount) : amount;
    const safeAmount = Number.isFinite(normalized) ? normalized : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(safeAmount);
  };

  const toNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const invoices = useMemo(() => invoicesData?.invoices ?? [], [invoicesData]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice: any) => {
      const total = toNumber(invoice.total) / 100;
      const dueDate = invoice.due_date ? new Date(invoice.due_date) : null;
      const startDate = filters.startDate ? new Date(filters.startDate) : null;
      const endDate = filters.endDate ? new Date(filters.endDate) : null;
      const minAmount = filters.minAmount ? toNumber(filters.minAmount) : null;
      const maxAmount = filters.maxAmount ? toNumber(filters.maxAmount) : null;

      if (startDate && dueDate && dueDate < startDate) return false;
      if (endDate && dueDate && dueDate > endDate) return false;
      if (minAmount !== null && total < minAmount) return false;
      if (maxAmount !== null && total > maxAmount) return false;

      return true;
    });
  }, [invoices, filters.startDate, filters.endDate, filters.minAmount, filters.maxAmount]);

  const overview = useMemo(() => {
    const stats = billingOverview || {};

    const totalInvoices = toNumber((stats as any).total_count);
    const totalRevenue = toNumber((stats as any).paid_amount) / 100;
    const totalAmount = toNumber((stats as any).total_amount) / 100;
    const openCount = toNumber((stats as any).open_count);
    const overdueCount = toNumber((stats as any).overdue_count);
    const averageInvoiceValue = totalInvoices > 0 ? totalAmount / totalInvoices : 0;
    const collectionRate = totalInvoices > 0 ? (toNumber((stats as any).paid_count) / totalInvoices) * 100 : 0;

    const now = new Date();
    const monthlyRevenue = invoices
      .filter((invoice: any) => {
        if (invoice.status !== 'paid' || !invoice.paid_at) return false;
        const paidAt = new Date(invoice.paid_at);
        return paidAt.getMonth() === now.getMonth() && paidAt.getFullYear() === now.getFullYear();
      })
      .reduce((sum: number, invoice: any) => sum + toNumber(invoice.amount_paid), 0) / 100;

    const paidWithTiming = invoices.filter((invoice: any) => invoice.status === 'paid' && invoice.issued_at && invoice.paid_at);
    const daysToPayment = paidWithTiming.length > 0
      ? paidWithTiming.reduce((sum: number, invoice: any) => {
          const issued = new Date(invoice.issued_at).getTime();
          const paid = new Date(invoice.paid_at).getTime();
          const days = Math.max(0, (paid - issued) / (1000 * 60 * 60 * 24));
          return sum + days;
        }, 0) / paidWithTiming.length
      : 0;

    return {
      totalRevenue,
      monthlyRevenue,
      pendingInvoices: openCount,
      overdueInvoices: overdueCount,
      totalInvoices,
      averageInvoiceValue,
      collectionRate,
      daysToPayment,
    };
  }, [billingOverview, invoices]);

  const collectedFeeActivity = useMemo(() => {
    const paidInvoices = Array.isArray(paidInvoicesForTrend) ? paidInvoicesForTrend : [];

    return paidInvoices
      .map((invoice: any) => {
        const platformFeeCents = toNumber(invoice.platform_fee);
        if (platformFeeCents <= 0) {
          return null;
        }

        const paidAtRaw = invoice.paid_at || invoice.updated_at || invoice.created_at;
        const paidAt = paidAtRaw ? new Date(paidAtRaw) : null;

        return {
          id: invoice.id,
          invoiceNumber: invoice.invoice_number || 'N/A',
          customerId: invoice.customer_id || invoice.customer?.id || null,
          customerName: [invoice.customer?.first_name, invoice.customer?.last_name].filter(Boolean).join(' ') || null,
          customerEmail: invoice.customer_details?.email || invoice.customer?.email || 'Unknown customer',
          paidAt,
          feeUsd: platformFeeCents / 100,
          amountPaidUsd: (toNumber(invoice.amount_paid) || toNumber(invoice.total)) / 100,
          stripeInvoiceId: invoice.stripe_invoice_id || null,
          paymentIntentId: invoice.payment_intent_id || null,
        };
      })
      .filter(Boolean)
      .sort((a: any, b: any) => (b.paidAt?.getTime() || 0) - (a.paidAt?.getTime() || 0))
      .slice(0, 10);
  }, [paidInvoicesForTrend]);

  const openInvoiceDetails = (invoiceId: string) => {
    const selectedEntry = collectedFeeActivity.find((entry: any) => entry.id === invoiceId) || null;
    setSelectedFeeInvoice(selectedEntry);
  };

  const openCustomerDrawer = (entry: any) => {
    if (!entry) return;

    setSelectedFeeCustomer({
      customerId: entry.customerId || null,
      customerName: entry.customerName || null,
      customerEmail: entry.customerEmail || 'Unknown customer',
    });
  };

  const selectedCustomerFeeEvents = useMemo(() => {
    if (!selectedFeeCustomer) {
      return [];
    }

    return collectedFeeActivity.filter((entry: any) => {
      if (selectedFeeCustomer.customerId && entry.customerId) {
        return entry.customerId === selectedFeeCustomer.customerId;
      }

      return entry.customerEmail === selectedFeeCustomer.customerEmail;
    });
  }, [collectedFeeActivity, selectedFeeCustomer]);

  const exportInvoiceCsv = () => {
    const headers: string[] = [
      'invoice_number',
      'customer_email',
      'status',
      'total_usd',
      'amount_due_usd',
      'due_date',
      'created_at',
    ];

    const rows: string[][] = filteredInvoices.map((invoice: any) => {
      const customerEmail = invoice.customer_details?.email || invoice.customer?.email || '';
      return [
        invoice.invoice_number,
        customerEmail,
        invoice.status,
        (toNumber(invoice.total) / 100).toFixed(2),
        (toNumber(invoice.amount_due) / 100).toFixed(2),
        invoice.due_date ? new Date(invoice.due_date).toISOString() : '',
        invoice.created_at ? new Date(invoice.created_at).toISOString() : '',
      ];
    });

    const csv = [headers, ...rows]
      .map((row: string[]) => row.map((value: string) => `"${String(value ?? '').replaceAll('"', '""')}"`).join(','))
      .join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `billing-invoices-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const sendOverdueReminders = async () => {
    const overdue = filteredInvoices.filter((invoice: any) => invoice.status === 'open' && invoice.due_date && new Date(invoice.due_date) < new Date());
    if (overdue.length === 0) {
      toast('No overdue invoices to remind');
      return;
    }

    try {
      await Promise.all(overdue.map((invoice: any) => adminAPI.sendInvoice(invoice.id)));
      toast.success(`Sent ${overdue.length} overdue reminder${overdue.length > 1 ? 's' : ''}`);
    } catch {
      toast.error('Failed to send one or more reminders');
    }
  };

  const getDisputeStatusColor = (status: string) => {
    switch (status) {
      case 'needs_response': return 'bg-error-container text-error-on-container';
      case 'under_review': return 'bg-warning-container text-warning-on-container';
      case 'won': return 'bg-success-container text-success-on-container';
      case 'lost': return 'bg-surface-container-low text-on-surface-variant';
      case 'warning_needs_response': return 'bg-orange-100 text-orange-800';
      default: return 'bg-surface-container-low text-on-surface-variant';
    }
  };

  const disputes = disputesData?.disputes || [];
  const disputePagination = disputesData?.pagination;

  return (
    <PageLayout>
      <AdminPageHeader
        eyebrow="Tenant billing"
        title="Billing Management"
        description="Manage invoices, payments, disputes, and fee activity from a single tenant billing surface."
        actions={activeTab === 'invoices' ? (
          <button
            onClick={() => setShowCreateInvoice(true)}
            className="btn-primary inline-flex items-center space-x-2"
          >
            <PlusIcon className="h-5 w-5" />
            <span>Create Invoice</span>
          </button>
        ) : undefined}
      />

      <AdminFilterCard className="mb-8">
        <nav className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveTab('invoices')}
            className={`admin-nav-link inline-flex items-center gap-2 rounded-pill px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'invoices' ? 'admin-nav-link-active' : ''
            }`}
          >
            <DocumentTextIcon className="h-5 w-5" />
            <span>Invoices</span>
          </button>
          <button
            onClick={() => setActiveTab('disputes')}
            className={`admin-nav-link inline-flex items-center gap-2 rounded-pill px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === 'disputes' ? 'admin-nav-link-active' : ''
            }`}
          >
            <ShieldExclamationIcon className="h-5 w-5" />
            <span>Disputes</span>
          </button>
        </nav>
      </AdminFilterCard>

      {activeTab === 'invoices' && (<>
      {overview && (
        <div className="mb-8">
          <AdminStatGrid>
            <AdminStatCard
              icon={<CurrencyDollarIcon className="h-7 w-7" />}
              label="Total Revenue"
              value={formatCurrency(overview.totalRevenue)}
              helper={`${formatCurrency(overview.monthlyRevenue)} this month`}
              tone="success"
            />
            <AdminStatCard
              icon={<DocumentTextIcon className="h-7 w-7" />}
              label="Total Invoices"
              value={overview.totalInvoices}
              helper={`${formatCurrency(overview.averageInvoiceValue)} avg value`}
              tone="info"
            />
            <AdminStatCard
              icon={<ExclamationTriangleIcon className="h-7 w-7" />}
              label="Pending / Overdue"
              value={overview.pendingInvoices + overview.overdueInvoices}
              helper={`${overview.overdueInvoices} overdue`}
              tone="warning"
            />
            <AdminStatCard
              icon={<ChartBarIcon className="h-7 w-7" />}
              label="Collection Rate"
              value={`${overview.collectionRate.toFixed(1)}%`}
              helper={`${overview.daysToPayment.toFixed(1)} avg days to pay`}
            />
          </AdminStatGrid>
        </div>
      )}

      <BillingRevenueTrendChart
        className="mb-8"
        source="tenant"
        showFeeComparison={showFeeComparison}
        days={14}
      />

      {showFeeComparison && (
        <AdminTableCard
          className="mb-8"
          title="Collected Fee Activity"
          description="Latest platform fee events with invoice and customer context."
          meta={<span>Showing last {collectedFeeActivity.length} entries</span>}
        >
          {collectedFeeActivity.length === 0 ? (
            <AdminEmptyState
              icon={<CurrencyDollarIcon className="h-6 w-6" />}
              title="No collected fees yet"
              description="Collected fee records will appear here once paid invoices begin generating platform fees."
            />
          ) : (
            <div className="overflow-x-auto">
              <table className="admin-table-grid min-w-full">
                <thead className="admin-table-head">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Date</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Invoice</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Collected Fee</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Payment Amount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Stripe Ref</th>
                  </tr>
                </thead>
                <tbody className="admin-table-body">
                  {collectedFeeActivity.map((entry: any) => (
                    <tr key={entry.id} className="admin-table-row">
                      <td className="px-4 py-3 text-sm text-on-surface">
                        {entry.paidAt ? entry.paidAt.toLocaleString() : 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-on-surface">
                        <button
                          type="button"
                          className="text-left hover:underline hover:text-primary-600"
                          onClick={() => openInvoiceDetails(entry.id)}
                        >
                          #{entry.invoiceNumber}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm text-on-surface">
                        <button
                          type="button"
                          className="text-left hover:underline hover:text-primary-600"
                          onClick={() => openCustomerDrawer(entry)}
                        >
                          {entry.customerEmail}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-sm font-semibold text-amber-700">{formatCurrency(entry.feeUsd)}</td>
                      <td className="px-4 py-3 text-sm text-on-surface">{formatCurrency(entry.amountPaidUsd)}</td>
                      <td className="px-4 py-3 text-xs text-on-surface-variant font-mono">
                        {entry.paymentIntentId || entry.stripeInvoiceId || 'N/A'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AdminTableCard>
      )}

      {selectedFeeInvoice && (
        <AdminModalShell
          title="Invoice Details"
          description="Live fee activity details for the selected paid invoice."
          onClose={() => setSelectedFeeInvoice(null)}
          maxWidthClassName="max-w-2xl"
        >
            <div className="grid grid-cols-1 gap-4 text-sm md:grid-cols-2">
              <div>
                <div className="text-on-surface-variant">Invoice</div>
                <div className="font-semibold text-on-surface">#{selectedFeeInvoice.invoiceNumber}</div>
              </div>
              <div>
                <div className="text-on-surface-variant">Customer</div>
                <div className="text-on-surface">{selectedFeeInvoice.customerEmail}</div>
              </div>
              <div>
                <div className="text-on-surface-variant">Paid At</div>
                <div className="text-on-surface">{selectedFeeInvoice.paidAt ? selectedFeeInvoice.paidAt.toLocaleString() : 'N/A'}</div>
              </div>
              <div>
                <div className="text-on-surface-variant">Collected Fee</div>
                <div className="font-semibold text-amber-700">{formatCurrency(selectedFeeInvoice.feeUsd)}</div>
              </div>
              <div>
                <div className="text-on-surface-variant">Payment Amount</div>
                <div className="text-on-surface">{formatCurrency(selectedFeeInvoice.amountPaidUsd)}</div>
              </div>
              <div>
                <div className="text-on-surface-variant">Stripe Ref</div>
                <div className="font-mono text-xs text-on-surface">{selectedFeeInvoice.paymentIntentId || selectedFeeInvoice.stripeInvoiceId || 'N/A'}</div>
              </div>
            </div>
        </AdminModalShell>
      )}

      <CustomerDetailDrawer
        open={Boolean(selectedFeeCustomer)}
        onClose={() => setSelectedFeeCustomer(null)}
        customer={selectedFeeCustomer}
        fallbackInvoices={selectedCustomerFeeEvents.map((entry: any) => ({
          id: entry.id,
          invoice_number: entry.invoiceNumber,
          status: 'paid',
          total: Math.round((entry.amountPaidUsd || 0) * 100),
          amount_paid: Math.round((entry.amountPaidUsd || 0) * 100),
          issued_at: entry.paidAt ? entry.paidAt.toISOString() : null,
          paid_at: entry.paidAt ? entry.paidAt.toISOString() : null,
          due_date: null,
          payment_intent_id: entry.paymentIntentId || null,
          stripe_invoice_id: entry.stripeInvoiceId || null,
        }))}
        sourceNote="This view is sourced from tenant paid invoice activity and Stripe references."
        subtitlePrefix="Customer ID"
      />

      

      {/* Quick Actions */}
      {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <button
          onClick={() => setShowCreateInvoice(true)}
          className="card p-4 text-left hover:shadow-elevated transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <PlusIcon className="h-6 w-6 text-blue-600" />
            <div>
              <h3 className="font-medium text-on-surface">Generate Invoices</h3>
              <p className="text-sm text-on-surface-variant">Create invoices for pending reservations</p>
            </div>
          </div>
        </button>

        <button
          onClick={sendOverdueReminders}
          className="card p-4 text-left hover:shadow-elevated transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <ExclamationTriangleIcon className="h-6 w-6 text-orange-600" />
            <div>
              <h3 className="font-medium text-on-surface">Overdue Follow-up</h3>
              <p className="text-sm text-on-surface-variant">Send reminders for overdue payments</p>
            </div>
          </div>
        </button>

        <button
          onClick={exportInvoiceCsv}
          className="card p-4 text-left hover:shadow-elevated transition-shadow"
        >
          <div className="flex items-center space-x-3">
            <ArrowDownTrayIcon className="h-6 w-6 text-green-600" />
            <div>
              <h3 className="font-medium text-on-surface">Export Reports</h3>
              <p className="text-sm text-on-surface-variant">Download billing and tax reports</p>
            </div>
          </div>
        </button>
      </div> */}

      {/* Filters */}
      <AdminFilterCard
        className="mb-6"
        title="Filter Invoices"
        icon={<FunnelIcon className="h-5 w-5" />}
      >
        <div className="flex items-center space-x-2 mb-4">
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Status</label>
            <select
              className="input-field"
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
            >
              <option value="">All Statuses</option>
              <option value="draft">Draft</option>
              <option value="open">Pending payment</option>
              <option value="paid">Paid</option>
              <option value="void">Void</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Customer ID</label>
            <input
              type="text"
              className="input-field"
              placeholder="Search customer..."
              value={filters.customerId}
              onChange={(e) => setFilters({ ...filters, customerId: e.target.value, page: 1 })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Start Date</label>
            <input
              type="date"
              className="input-field"
              value={filters.startDate}
              onChange={(e) => setFilters({ ...filters, startDate: e.target.value, page: 1 })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">End Date</label>
            <input
              type="date"
              className="input-field"
              value={filters.endDate}
              onChange={(e) => setFilters({ ...filters, endDate: e.target.value, page: 1 })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Min Amount</label>
            <input
              type="number"
              className="input-field"
              placeholder="0.00"
              value={filters.minAmount}
              onChange={(e) => setFilters({ ...filters, minAmount: e.target.value, page: 1 })}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Max Amount</label>
            <input
              type="number"
              className="input-field"
              placeholder="1000.00"
              value={filters.maxAmount}
              onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value, page: 1 })}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-between items-center">
          <button
            onClick={() => setFilters({ status: '', customerId: '', startDate: '', endDate: '', minAmount: '', maxAmount: '', page: 1, limit: 25 })}
            className="btn-secondary"
          >
            Clear Filters
          </button>

          <div className="flex space-x-2">
            <button onClick={exportInvoiceCsv} className="btn-secondary flex items-center space-x-2">
              <ArrowDownTrayIcon className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
          </div>
        </div>
      </AdminFilterCard>

      <AdminTableCard title="Invoice Management">
        {invoicesLoading ? (
          <LoadingSpinner className="py-20" />
        ) : (
          <div className="overflow-x-auto">
            <table className="admin-table-grid min-w-full">
              <thead className="admin-table-head">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Invoice
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="admin-table-body">
                {filteredInvoices.map((invoice: any) => {
                  const dueDate = invoice.due_date ? new Date(invoice.due_date) : null;
                  const isOverdue = invoice.status === 'open' && dueDate && dueDate < new Date();
                  const customerEmail = invoice.customer_details?.email || invoice.customer?.email || 'Unknown customer';
                  const customerId = invoice.customer_id || invoice.customer?.id || 'N/A';

                  return (
                  <tr key={invoice.id} className="admin-table-row">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(invoice.status)}
                        <div className="ml-3">
                          <div className="text-sm font-medium text-on-surface">
                            #{invoice.invoice_number}
                          </div>
                          <div className="text-sm text-on-surface-variant">
                            {invoice.created_at ? new Date(invoice.created_at).toLocaleDateString() : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <UserIcon className="h-5 w-5 text-on-surface-variant mr-2" />
                        <div>
                          <div className="text-sm font-medium text-on-surface">
                            {customerEmail}
                          </div>
                          <div className="text-sm text-on-surface-variant">
                            ID: {customerId}
                          </div>
                        </div>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-on-surface">
                      {formatCurrency(toNumber(invoice.total) / 100)}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`admin-soft-pill inline-flex rounded-pill px-2 py-1 text-xs font-semibold leading-5 ${getStatusColor(isOverdue ? 'overdue' : invoice.status)}`}>
                        {getInvoiceStatusLabel(invoice.status, Boolean(isOverdue))}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface">
                      {dueDate ? dueDate.toLocaleDateString() : 'N/A'}
                      {isOverdue && dueDate && (
                        <div className="text-xs text-red-600 font-medium">
                          {Math.ceil((Date.now() - dueDate.getTime()) / (1000 * 60 * 60 * 24))} days overdue
                        </div>
                      )}
                    </td>
                    
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex space-x-2">
                        {invoice.status === 'open' && (
                          <button
                            onClick={() => handleMarkPaid(invoice.id)}
                            className="text-purple-600 hover:text-purple-900"
                            title="Mark as Paid"
                            disabled={markPaidMutation.isLoading}
                          >
                            ✓
                          </button>
                        )}

                        {(invoice.status === 'open' || isOverdue) && (
                          <button
                            onClick={() => sendInvoiceMutation.mutate(invoice.id)}
                            className="text-orange-600 hover:text-orange-900"
                            title="Send Reminder"
                            disabled={sendInvoiceMutation.isLoading}
                          >
                            📧
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )})}
              </tbody>
            </table>
          </div>
        )}
      </AdminTableCard>

      </>)}

      {/* Disputes Tab */}
      {activeTab === 'disputes' && (
        <div>
          {/* Dispute Filters */}
          <AdminFilterCard className="mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-on-surface-variant">Status</label>
                <select
                  className="input-field w-48"
                  value={disputeFilters.status}
                  onChange={(e) => setDisputeFilters({ ...disputeFilters, status: e.target.value, page: 1 })}
                >
                  <option value="">All Statuses</option>
                  <option value="needs_response">Needs Response</option>
                  <option value="under_review">Under Review</option>
                  <option value="won">Won</option>
                  <option value="lost">Lost</option>
                </select>
              </div>
            </div>
          </AdminFilterCard>

          <AdminTableCard title="Payment Disputes">
            {disputesLoading ? (
              <LoadingSpinner className="py-20" />
            ) : disputes.length === 0 ? (
              <AdminEmptyState
                icon={<ShieldExclamationIcon className="h-6 w-6" />}
                title="No disputes found"
                description="Disputes from Stripe will appear here automatically."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table-grid min-w-full">
                  <thead className="admin-table-head">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Dispute</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Reason</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Evidence Due</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-on-surface-variant uppercase tracking-wider">Created</th>
                    </tr>
                  </thead>
                  <tbody className="admin-table-body">
                    {disputes.map((dispute: any) => (
                      <tr key={dispute.id} className="admin-table-row">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-on-surface">{dispute.stripe_dispute_id}</div>
                          <div className="text-xs text-on-surface-variant">{dispute.stripe_charge_id}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-on-surface">
                          {formatCurrency(dispute.amount / 100)} {dispute.currency}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                          {dispute.reason?.replace(/_/g, ' ')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`admin-soft-pill inline-flex rounded-pill px-2 py-1 text-xs font-semibold leading-5 ${getDisputeStatusColor(dispute.status)}`}>
                            {dispute.status?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                          {dispute.evidence_due_by ? (
                            <div>
                              <div>{new Date(dispute.evidence_due_by).toLocaleDateString()}</div>
                              {new Date(dispute.evidence_due_by) < new Date() && dispute.status === 'needs_response' && (
                                <div className="text-xs text-red-600 font-medium">Overdue</div>
                              )}
                            </div>
                          ) : (
                            <span className="text-on-surface-variant">N/A</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                          {new Date(dispute.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Dispute Pagination */}
            {disputePagination && disputePagination.totalPages > 1 && (
              <div className="px-6 py-4 flex items-center justify-between">
                <p className="text-sm text-on-surface-variant">
                  Page {disputePagination.page} of {disputePagination.totalPages} ({disputePagination.total} total)
                </p>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setDisputeFilters({ ...disputeFilters, page: disputeFilters.page - 1 })}
                    disabled={disputeFilters.page <= 1}
                    className="btn-secondary text-sm disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setDisputeFilters({ ...disputeFilters, page: disputeFilters.page + 1 })}
                    disabled={disputeFilters.page >= disputePagination.totalPages}
                    className="btn-secondary text-sm disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </AdminTableCard>
        </div>
      )}

      {/* Create Invoice Modal */}
      {showCreateInvoice && (
        <CreateInvoiceModal
          onClose={() => setShowCreateInvoice(false)}
          onSuccess={() => {
            setShowCreateInvoice(false);
            // Refetch data after invoice created
            refetchInvoices();
            toast.success('Invoice list refreshed');
          }}
        />
      )}
    </PageLayout>
  );
}

export default AdminBilling;
